import { SessionToolSelection } from "./toolCatalog.ts";
import { ThreadId } from "./baseSchemas.ts";
import { ClaudeAccountSignInRpcGroup } from "./claudeAccountSignIn.ts";
import { LocalModelsRpcGroup } from "./localModels.ts";
import { WorldWatchRpcGroup } from "./worldWatch.ts";
import { CodexAccountRpcGroup } from "./codexAccounts.ts";
import {
  CanvasDoorViewRequest,
  CanvasDoorActionRequest,
  CanvasDoorResult,
  CanvasDoorResourceRequest,
  CanvasDoorResourceEvent,
  CanvasDoorDataRequest,
} from "./canvasDoor.ts";
import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import * as RpcMiddleware from "effect/unstable/rpc/RpcMiddleware";
import { AgentContextResolution } from "./contextResolution.ts";

import { AudioTapError, AudioTapEvent, AudioTapStartInput, AudioTapStatus } from "./audioTap.ts";
import { ExternalLauncherError, LaunchEditorInput } from "./editor.ts";
import {
  AuthAccessStreamError,
  AuthAccessStreamEvent,
  EnvironmentAuthorizationError,
} from "./auth.ts";
import { DictationRpcGroup, WsDictationStartRpc } from "./dictation.ts";
import { ReadbackRpcGroup, WsReadbackSpeakRpc } from "./readback.ts";
import { ClientActivityReportInput } from "./background.ts";
import {
  FilesystemBrowseInput,
  FilesystemBrowseResult,
  FilesystemBrowseError,
} from "./filesystem.ts";
import { AssetAccessError, AssetCreateUrlInput, AssetCreateUrlResult } from "./assets.ts";
import {
  GitActionProgressEvent,
  VcsSwitchRefInput,
  VcsSwitchRefResult,
  GitCommandError,
  VcsCreateRefInput,
  VcsCreateRefResult,
  VcsCreateWorktreeInput,
  VcsCreateWorktreeResult,
  VcsInitInput,
  VcsListRefsInput,
  VcsListRefsResult,
  GitManagerServiceError,
  VcsPullInput,
  VcsPullResult,
  VcsRemoveWorktreeInput,
  GitRunStackedActionInput,
  VcsStatusInput,
  VcsStatusResult,
  VcsStatusStreamEvent,
} from "./git.ts";
import {
  ReviewDiffFileContentsInput,
  ReviewDiffFileContentsResult,
  ReviewDiffPreviewError,
  ReviewDiffPreviewInput,
  ReviewDiffPreviewResult,
} from "./review.ts";
import { KeybindingsConfigError } from "./keybindings.ts";
import {
  ClientOrchestrationCommand,
  ORCHESTRATION_WS_METHODS,
  OrchestrationDispatchCommandError,
  OrchestrationGetFullThreadDiffError,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationGetSnapshotError,
  OrchestrationSearchThreadsError,
  OrchestrationSearchThreadsInput,
  OrchestrationGetTurnDiffError,
  OrchestrationGetTurnDiffInput,
  OrchestrationRpcSchemas,
  OrchestrationGetWorkflowScriptError,
} from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import {
  RelayClientInstallFailedError,
  RelayClientInstallProgressEventSchema,
  RelayClientStatusSchema,
} from "./relayClient.ts";
import {
  ProjectListEntriesError,
  ProjectListEntriesInput,
  ProjectListEntriesResult,
  ProjectReadFileError,
  ProjectReadFileInput,
  ProjectReadFileResult,
  ProjectSearchContentsError,
  ProjectSearchContentsInput,
  ProjectSearchContentsResult,
  ProjectSearchEntriesError,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
  ProjectWriteFileError,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
} from "./project.ts";
import {
  TerminalAttachInput,
  TerminalAttachStreamEvent,
  TerminalClearInput,
  TerminalCloseInput,
  TerminalError,
  TerminalEvent,
  TerminalMetadataStreamEvent,
  TerminalOpenInput,
  TerminalResizeInput,
  TerminalRestartInput,
  TerminalSessionSnapshot,
  TerminalWriteInput,
} from "./terminal.ts";
import {
  DiscoveredLocalServerList,
  ConfiguredLocalServerUrls,
  PreviewCloseInput,
  PreviewError,
  PreviewEvent,
  PreviewListInput,
  PreviewListResult,
  PreviewNavigateInput,
  PreviewOpenInput,
  PreviewRefreshInput,
  PreviewReportStatusInput,
  PreviewResizeInput,
  PreviewSessionSnapshot,
} from "./preview.ts";
import {
  PreviewAutomationError,
  PreviewAutomationClaim,
  PreviewAutomationHost,
  PreviewAutomationHostFocus,
  PreviewAutomationResponse,
  PreviewAutomationStreamEvent,
} from "./previewAutomation.ts";
import {
  CloudBrowserControlInput,
  CloudBrowserFrame,
  CloudBrowserControlError,
} from "./cloudBrowser.ts";
import {
  ServerConfigStreamEvent,
  ServerConfig,
  ServerProviderUpdateError,
  ServerProviderUpdateInput,
  ServerLifecycleStreamEvent,
  ServerRemoveKeybindingInput,
  ServerRemoveKeybindingResult,
  ServerProviderUpdatedPayload,
  ServerSelfUpdateError,
  ServerSelfUpdateInput,
  ServerSelfUpdateProgressEvent,
  ServerSelfUpdateResult,
  ServerTraceDiagnosticsResult,
  ServerProcessDiagnosticsResult,
  ServerProcessResourceHistoryInput,
  ServerProcessResourceHistoryResult,
  ServerSignalProcessInput,
  ServerSignalProcessResult,
  ServerUpsertKeybindingInput,
  ServerUpsertKeybindingResult,
} from "./server.ts";
import {
  ResourceTelemetryHistory,
  ResourceTelemetryHistoryInput,
  ResourceTelemetryRetryResult,
  ResourceTelemetrySnapshot,
} from "./resourceTelemetry.ts";
import {
  SpotifyCollectionRef,
  SpotifyCollectionResultEvent,
  SpotifyDevice,
  SpotifyEngineCallError,
  SpotifyEngineView,
  SpotifyHomeResultEvent,
  SpotifyLibraryResultEvent,
  SpotifyTrack,
  SpotifyUserCommand,
} from "./spotifyProtocol.ts";
import {
  MusicCreatePlaylistInput,
  MusicCollectionCard,
  MusicCollection,
  MusicDevice,
  MusicEngineCallError,
  MusicEngineView,
  MusicHome,
  MusicItemRef,
  MusicLibrary,
  MusicReadInput,
  MusicSearchAnswer,
  MusicUserCommand,
} from "./musicProtocol.ts";
import {
  UsageAccountCapacityReport,
  UsageReadError,
  UsageSummary,
  UsageSummaryInput,
} from "./usage.ts";
import { ServerSettings, ServerSettingsError, ServerSettingsPatch } from "./settings.ts";
import { VcsError } from "./vcs.ts";
import {
  NeucleosDoorSessionAttachRequest,
  NeucleosDoorSessionDiscoveryRequest,
  NeucleosDoorSessionDiscovery,
  NeucleosDoorContextSelection,
  NeucleosDoorPrepareContextSelectionRequest,
  NeucleosDoorSaveSessionToolsRequest,
  NeucleosDoorSessionBinding,
  NeucleosDoorSessionError,
  NeucleosDoorLiveVoiceRequest,
  NeucleosDoorLiveVoiceResult,
} from "./neucleosDoor.ts";

export const WS_METHODS = {
  // Project registry methods
  projectsListEntries: "projects.listEntries",
  projectsReadFile: "projects.readFile",
  projectsSearchContents: "projects.searchContents",
  projectsSearchEntries: "projects.searchEntries",
  projectsWriteFile: "projects.writeFile",

  // Shell methods
  shellOpenInEditor: "shell.openInEditor",

  // Filesystem methods
  filesystemBrowse: "filesystem.browse",
  assetsCreateUrl: "assets.createUrl",

  // VCS methods
  vcsPull: "vcs.pull",
  vcsRefreshStatus: "vcs.refreshStatus",
  vcsListRefs: "vcs.listRefs",
  vcsCreateWorktree: "vcs.createWorktree",
  vcsRemoveWorktree: "vcs.removeWorktree",
  vcsCreateRef: "vcs.createRef",
  vcsSwitchRef: "vcs.switchRef",
  vcsInit: "vcs.init",

  // Git workflow methods
  gitRunStackedAction: "git.runStackedAction",

  // Review methods
  reviewGetDiffPreview: "review.getDiffPreview",
  reviewGetDiffFileContents: "review.getDiffFileContents",

  // Terminal methods
  terminalOpen: "terminal.open",
  terminalAttach: "terminal.attach",
  terminalWrite: "terminal.write",
  terminalResize: "terminal.resize",
  terminalClear: "terminal.clear",
  terminalRestart: "terminal.restart",
  terminalClose: "terminal.close",

  // Preview methods
  previewOpen: "preview.open",
  cloudBrowserControl: "cloudBrowser.control",
  cloudBrowserWatch: "cloudBrowser.watch",
  previewNavigate: "preview.navigate",
  previewResize: "preview.resize",
  previewRefresh: "preview.refresh",
  previewClose: "preview.close",
  previewList: "preview.list",
  previewReportStatus: "preview.reportStatus",
  previewAutomationConnect: "previewAutomation.connect",
  previewAutomationClaim: "previewAutomation.claim",
  previewAutomationRespond: "previewAutomation.respond",
  previewAutomationFocusHost: "previewAutomation.focusHost",

  // Server meta
  serverProbe: "server.probe",
  serverGetConfig: "server.getConfig",
  serverRefreshProviders: "server.refreshProviders",
  serverUpdateProvider: "server.updateProvider",
  serverUpdateServer: "server.updateServer",
  serverUpdateServerWithProgress: "server.updateServerWithProgress",
  serverUpsertKeybinding: "server.upsertKeybinding",
  serverRemoveKeybinding: "server.removeKeybinding",
  serverGetSettings: "server.getSettings",
  serverUpdateSettings: "server.updateSettings",
  serverGetTraceDiagnostics: "server.getTraceDiagnostics",
  serverGetProcessDiagnostics: "server.getProcessDiagnostics",
  serverGetProcessResourceHistory: "server.getProcessResourceHistory",
  serverGetResourceTelemetryHistory: "server.getResourceTelemetryHistory",
  serverRetryResourceTelemetry: "server.retryResourceTelemetry",
  musicSendCommand: "music.sendCommand",
  musicSearch: "music.search",
  musicListDevices: "music.listDevices",
  musicGetLibrary: "music.getLibrary",
  musicGetHome: "music.getHome",
  musicGetCollection: "music.getCollection",
  musicCreatePlaylist: "music.createPlaylist",
  spotifySendCommand: "spotify.sendCommand",
  spotifySearch: "spotify.search",
  spotifyListDevices: "spotify.listDevices",
  spotifyGetLibrary: "spotify.getLibrary",
  spotifyGetHome: "spotify.getHome",
  spotifyGetCollection: "spotify.getCollection",
  audioTapStart: "audioTap.start",
  audioTapStop: "audioTap.stop",
  serverSignalProcess: "server.signalProcess",
  serverReportClientActivity: "server.reportClientActivity",
  serverGetUsageSummary: "server.getUsageSummary",
  serverGetAccountCapacity: "server.getAccountCapacity",

  // One authenticated Door connection binds to one durable World Session.
  neucleosDoorSessionAttach: "neucleosDoor.session.attach",
  neucleosDoorSessionRenew: "neucleosDoor.session.renew",
  neucleosDoorSessionDiscover: "neucleosDoor.session.discover",
  neucleosDoorSessionReadTools: "neucleosDoor.session.readTools",
  neucleosDoorSessionSaveTools: "neucleosDoor.session.saveTools",
  neucleosDoorSessionReadContextSelection: "neucleosDoor.session.readContextSelection",
  neucleosDoorSessionPrepareContextSelection: "neucleosDoor.session.prepareContextSelection",
  // The paired Pocket's Live Talk door: one operation per call over the binding.
  neucleosDoorLiveVoice: "neucleosDoor.liveVoice",
  neucleosDoorCanvas: "neucleosDoor.canvas",
  neucleosDoorCanvasAction: "neucleosDoor.canvas.action",
  neucleosDoorCanvasResource: "neucleosDoor.canvas.resource",
  neucleosDoorCanvasData: "neucleosDoor.canvas.data",

  // Cloud environment methods
  cloudGetRelayClientStatus: "cloud.getRelayClientStatus",
  cloudInstallRelayClient: "cloud.installRelayClient",

  // Streaming subscriptions
  subscribeVcsStatus: "subscribeVcsStatus",
  subscribeTerminalEvents: "subscribeTerminalEvents",
  subscribeTerminalMetadata: "subscribeTerminalMetadata",
  subscribePreviewEvents: "subscribePreviewEvents",
  subscribeDiscoveredLocalServers: "subscribeDiscoveredLocalServers",
  subscribeServerConfig: "subscribeServerConfig",
  subscribeServerLifecycle: "subscribeServerLifecycle",
  subscribeAuthAccess: "subscribeAuthAccess",
  subscribeResourceTelemetry: "subscribeResourceTelemetry",
  subscribeMusicView: "subscribeMusicView",
  subscribeSpotifyView: "subscribeSpotifyView",
  subscribeAudioTap: "subscribeAudioTap",
} as const;

export const WsServerUpsertKeybindingRpc = Rpc.make(WS_METHODS.serverUpsertKeybinding, {
  payload: ServerUpsertKeybindingInput,
  success: ServerUpsertKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerRemoveKeybindingRpc = Rpc.make(WS_METHODS.serverRemoveKeybinding, {
  payload: ServerRemoveKeybindingInput,
  success: ServerRemoveKeybindingResult,
  error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});

export const WsServerProbeRpc = Rpc.make(WS_METHODS.serverProbe, {
  payload: Schema.Struct({}),
  success: Schema.Struct({}),
  error: EnvironmentAuthorizationError,
});

export const WsNeucleosDoorSessionAttachRpc = Rpc.make(WS_METHODS.neucleosDoorSessionAttach, {
  payload: NeucleosDoorSessionAttachRequest,
  success: NeucleosDoorSessionBinding,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});

export const WsNeucleosDoorSessionRenewRpc = Rpc.make(WS_METHODS.neucleosDoorSessionRenew, {
  payload: Schema.Record(Schema.String, Schema.Never),
  success: NeucleosDoorSessionBinding,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});

export const WsNeucleosDoorSessionDiscoverRpc = Rpc.make(WS_METHODS.neucleosDoorSessionDiscover, {
  payload: NeucleosDoorSessionDiscoveryRequest,
  success: NeucleosDoorSessionDiscovery,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});

export const WsNeucleosDoorSessionReadContextSelectionRpc = Rpc.make(
  WS_METHODS.neucleosDoorSessionReadContextSelection,
  {
    payload: Schema.Struct({}).annotate({ parseOptions: { onExcessProperty: "error" } }),
    success: NeucleosDoorContextSelection,
    error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
  },
);

export const WsNeucleosDoorSessionReadToolsRpc = Rpc.make(WS_METHODS.neucleosDoorSessionReadTools, {
  payload: Schema.Struct({}).annotate({ parseOptions: { onExcessProperty: "error" } }),
  success: SessionToolSelection,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});
export const WsNeucleosDoorSessionSaveToolsRpc = Rpc.make(WS_METHODS.neucleosDoorSessionSaveTools, {
  payload: NeucleosDoorSaveSessionToolsRequest,
  success: SessionToolSelection,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});

export const NeucleosDoorPreparedContextSelection = Schema.Struct({
  selection: NeucleosDoorContextSelection,
  resolution: AgentContextResolution,
});
export type NeucleosDoorPreparedContextSelection = typeof NeucleosDoorPreparedContextSelection.Type;

export const WsNeucleosDoorSessionPrepareContextSelectionRpc = Rpc.make(
  WS_METHODS.neucleosDoorSessionPrepareContextSelection,
  {
    payload: NeucleosDoorPrepareContextSelectionRequest,
    success: NeucleosDoorPreparedContextSelection,
    error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
  },
);

export const WsNeucleosDoorLiveVoiceRpc = Rpc.make(WS_METHODS.neucleosDoorLiveVoice, {
  payload: NeucleosDoorLiveVoiceRequest,
  success: NeucleosDoorLiveVoiceResult,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});

export const WsNeucleosDoorCanvasRpc = Rpc.make(WS_METHODS.neucleosDoorCanvas, {
  payload: CanvasDoorViewRequest,
  success: CanvasDoorResult,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});
export const WsNeucleosDoorCanvasActionRpc = Rpc.make(WS_METHODS.neucleosDoorCanvasAction, {
  payload: CanvasDoorActionRequest,
  success: CanvasDoorResult,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
});
export const WsNeucleosDoorCanvasResourceRpc = Rpc.make(WS_METHODS.neucleosDoorCanvasResource, {
  payload: CanvasDoorResourceRequest,
  success: CanvasDoorResourceEvent,
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
  stream: true,
});
export const WsNeucleosDoorCanvasDataRpc = Rpc.make(WS_METHODS.neucleosDoorCanvasData, {
  payload: CanvasDoorDataRequest,
  success: Schema.String.check(Schema.isMaxLength(2 * 1024 * 1024)),
  error: Schema.Union([NeucleosDoorSessionError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
  payload: Schema.Struct({}),
  success: ServerConfig,
  error: Schema.Union([
    KeybindingsConfigError,
    ServerSettingsError,
    EnvironmentAuthorizationError,
    NeucleosDoorSessionError,
  ]),
});

export const WsServerRefreshProvidersRpc = Rpc.make(WS_METHODS.serverRefreshProviders, {
  payload: Schema.Struct({
    /**
     * When supplied, only refresh this specific provider instance. When
     * omitted, refresh all configured instances — the legacy `refresh()`
     * behaviour retained for transports that still dispatch untargeted
     * refreshes.
     */
    instanceId: Schema.optional(ProviderInstanceId),
  }),
  success: ServerProviderUpdatedPayload,
  error: EnvironmentAuthorizationError,
});

export const WsServerUpdateProviderRpc = Rpc.make(WS_METHODS.serverUpdateProvider, {
  payload: ServerProviderUpdateInput,
  success: ServerProviderUpdatedPayload,
  error: Schema.Union([ServerProviderUpdateError, EnvironmentAuthorizationError]),
});

export const WsServerUpdateServerRpc = Rpc.make(WS_METHODS.serverUpdateServer, {
  payload: ServerSelfUpdateInput,
  success: ServerSelfUpdateResult,
  error: Schema.Union([ServerSelfUpdateError, EnvironmentAuthorizationError]),
});

export const WsServerUpdateServerWithProgressRpc = Rpc.make(
  WS_METHODS.serverUpdateServerWithProgress,
  {
    payload: ServerSelfUpdateInput,
    success: ServerSelfUpdateProgressEvent,
    error: Schema.Union([ServerSelfUpdateError, EnvironmentAuthorizationError]),
    stream: true,
  },
);

export const WsServerGetSettingsRpc = Rpc.make(WS_METHODS.serverGetSettings, {
  payload: Schema.Struct({}),
  success: ServerSettings,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsServerUpdateSettingsRpc = Rpc.make(WS_METHODS.serverUpdateSettings, {
  payload: Schema.Struct({ patch: ServerSettingsPatch }),
  success: ServerSettings,
  error: Schema.Union([ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsServerGetTraceDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerTraceDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetProcessDiagnostics, {
  payload: Schema.Struct({}),
  success: ServerProcessDiagnosticsResult,
  error: EnvironmentAuthorizationError,
});

export const WsServerGetProcessResourceHistoryRpc = Rpc.make(
  WS_METHODS.serverGetProcessResourceHistory,
  {
    payload: ServerProcessResourceHistoryInput,
    success: ServerProcessResourceHistoryResult,
    error: EnvironmentAuthorizationError,
  },
);

export const WsServerGetResourceTelemetryHistoryRpc = Rpc.make(
  WS_METHODS.serverGetResourceTelemetryHistory,
  {
    payload: ResourceTelemetryHistoryInput,
    success: ResourceTelemetryHistory,
    error: EnvironmentAuthorizationError,
  },
);

export const WsServerRetryResourceTelemetryRpc = Rpc.make(WS_METHODS.serverRetryResourceTelemetry, {
  payload: Schema.Struct({}),
  success: ResourceTelemetryRetryResult,
  error: EnvironmentAuthorizationError,
});

export const WsMusicSendCommandRpc = Rpc.make(WS_METHODS.musicSendCommand, {
  payload: Schema.Struct({ command: MusicUserCommand }),
  success: Schema.Void,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicSearchRpc = Rpc.make(WS_METHODS.musicSearch, {
  payload: Schema.Struct({ query: Schema.String }),
  success: MusicSearchAnswer,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicListDevicesRpc = Rpc.make(WS_METHODS.musicListDevices, {
  payload: MusicReadInput,
  success: Schema.Struct({ devices: Schema.Array(MusicDevice) }),
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicGetLibraryRpc = Rpc.make(WS_METHODS.musicGetLibrary, {
  payload: MusicReadInput,
  success: MusicLibrary,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicGetHomeRpc = Rpc.make(WS_METHODS.musicGetHome, {
  payload: MusicReadInput,
  success: MusicHome,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicCreatePlaylistRpc = Rpc.make(WS_METHODS.musicCreatePlaylist, {
  payload: MusicCreatePlaylistInput,
  success: MusicCollectionCard,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsMusicGetCollectionRpc = Rpc.make(WS_METHODS.musicGetCollection, {
  payload: Schema.Struct({ ref: MusicItemRef }),
  success: MusicCollection,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifySendCommandRpc = Rpc.make(WS_METHODS.spotifySendCommand, {
  payload: Schema.Struct({ command: SpotifyUserCommand }),
  success: Schema.Void,
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifySearchRpc = Rpc.make(WS_METHODS.spotifySearch, {
  payload: Schema.Struct({ query: Schema.String }),
  success: Schema.Struct({ tracks: Schema.Array(SpotifyTrack) }),
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifyListDevicesRpc = Rpc.make(WS_METHODS.spotifyListDevices, {
  payload: Schema.Struct({}),
  success: Schema.Struct({ devices: Schema.Array(SpotifyDevice) }),
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifyGetLibraryRpc = Rpc.make(WS_METHODS.spotifyGetLibrary, {
  payload: Schema.Struct({}),
  success: SpotifyLibraryResultEvent,
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifyGetHomeRpc = Rpc.make(WS_METHODS.spotifyGetHome, {
  payload: Schema.Struct({}),
  success: SpotifyHomeResultEvent,
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsSpotifyGetCollectionRpc = Rpc.make(WS_METHODS.spotifyGetCollection, {
  payload: Schema.Struct({ collection: SpotifyCollectionRef }),
  success: SpotifyCollectionResultEvent,
  error: Schema.Union([SpotifyEngineCallError, EnvironmentAuthorizationError]),
});

export const WsServerGetUsageSummaryRpc = Rpc.make(WS_METHODS.serverGetUsageSummary, {
  payload: UsageSummaryInput,
  success: UsageSummary,
  error: Schema.Union([EnvironmentAuthorizationError, UsageReadError]),
});

/** SAU-CAN-17: one capacity record per stable subscription account, no scan. */
export const WsServerGetAccountCapacityRpc = Rpc.make(WS_METHODS.serverGetAccountCapacity, {
  payload: Schema.Struct({}),
  success: UsageAccountCapacityReport,
  error: Schema.Union([EnvironmentAuthorizationError, UsageReadError]),
});

export const WsServerSignalProcessRpc = Rpc.make(WS_METHODS.serverSignalProcess, {
  payload: ServerSignalProcessInput,
  success: ServerSignalProcessResult,
  error: EnvironmentAuthorizationError,
});

export const WsCloudGetRelayClientStatusRpc = Rpc.make(WS_METHODS.cloudGetRelayClientStatus, {
  payload: Schema.Struct({}),
  success: RelayClientStatusSchema,
  error: EnvironmentAuthorizationError,
});

export const WsCloudInstallRelayClientRpc = Rpc.make(WS_METHODS.cloudInstallRelayClient, {
  payload: Schema.Struct({}),
  success: RelayClientInstallProgressEventSchema,
  error: Schema.Union([RelayClientInstallFailedError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsServerReportClientActivityRpc = Rpc.make(WS_METHODS.serverReportClientActivity, {
  payload: ClientActivityReportInput,
  error: EnvironmentAuthorizationError,
});

export const WsProjectsSearchEntriesRpc = Rpc.make(WS_METHODS.projectsSearchEntries, {
  payload: ProjectSearchEntriesInput,
  success: ProjectSearchEntriesResult,
  error: Schema.Union([ProjectSearchEntriesError, EnvironmentAuthorizationError]),
});

export const WsProjectsSearchContentsRpc = Rpc.make(WS_METHODS.projectsSearchContents, {
  payload: ProjectSearchContentsInput,
  success: ProjectSearchContentsResult,
  error: Schema.Union([ProjectSearchContentsError, EnvironmentAuthorizationError]),
});

export const WsProjectsListEntriesRpc = Rpc.make(WS_METHODS.projectsListEntries, {
  payload: ProjectListEntriesInput,
  success: ProjectListEntriesResult,
  error: Schema.Union([ProjectListEntriesError, EnvironmentAuthorizationError]),
});

export const WsProjectsReadFileRpc = Rpc.make(WS_METHODS.projectsReadFile, {
  payload: ProjectReadFileInput,
  success: ProjectReadFileResult,
  error: Schema.Union([ProjectReadFileError, EnvironmentAuthorizationError]),
});

export const WsProjectsWriteFileRpc = Rpc.make(WS_METHODS.projectsWriteFile, {
  payload: ProjectWriteFileInput,
  success: ProjectWriteFileResult,
  error: Schema.Union([ProjectWriteFileError, EnvironmentAuthorizationError]),
});

export const WsShellOpenInEditorRpc = Rpc.make(WS_METHODS.shellOpenInEditor, {
  payload: LaunchEditorInput,
  error: Schema.Union([ExternalLauncherError, EnvironmentAuthorizationError]),
});

export const WsFilesystemBrowseRpc = Rpc.make(WS_METHODS.filesystemBrowse, {
  payload: FilesystemBrowseInput,
  success: FilesystemBrowseResult,
  error: Schema.Union([FilesystemBrowseError, EnvironmentAuthorizationError]),
});

export const WsAssetsCreateUrlRpc = Rpc.make(WS_METHODS.assetsCreateUrl, {
  payload: AssetCreateUrlInput,
  success: AssetCreateUrlResult,
  error: Schema.Union([AssetAccessError, EnvironmentAuthorizationError]),
});

export const WsSubscribeVcsStatusRpc = Rpc.make(WS_METHODS.subscribeVcsStatus, {
  payload: VcsStatusInput,
  success: VcsStatusStreamEvent,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsVcsPullRpc = Rpc.make(WS_METHODS.vcsPull, {
  payload: VcsPullInput,
  success: VcsPullResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsRefreshStatusRpc = Rpc.make(WS_METHODS.vcsRefreshStatus, {
  payload: VcsStatusInput,
  success: VcsStatusResult,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
});

export const WsGitRunStackedActionRpc = Rpc.make(WS_METHODS.gitRunStackedAction, {
  payload: GitRunStackedActionInput,
  success: GitActionProgressEvent,
  error: Schema.Union([GitManagerServiceError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsVcsListRefsRpc = Rpc.make(WS_METHODS.vcsListRefs, {
  payload: VcsListRefsInput,
  success: VcsListRefsResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsCreateWorktreeRpc = Rpc.make(WS_METHODS.vcsCreateWorktree, {
  payload: VcsCreateWorktreeInput,
  success: VcsCreateWorktreeResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsRemoveWorktreeRpc = Rpc.make(WS_METHODS.vcsRemoveWorktree, {
  payload: VcsRemoveWorktreeInput,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsCreateRefRpc = Rpc.make(WS_METHODS.vcsCreateRef, {
  payload: VcsCreateRefInput,
  success: VcsCreateRefResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsSwitchRefRpc = Rpc.make(WS_METHODS.vcsSwitchRef, {
  payload: VcsSwitchRefInput,
  success: VcsSwitchRefResult,
  error: Schema.Union([GitCommandError, EnvironmentAuthorizationError]),
});

export const WsVcsInitRpc = Rpc.make(WS_METHODS.vcsInit, {
  payload: VcsInitInput,
  error: Schema.Union([VcsError, EnvironmentAuthorizationError]),
});

/**
 * Ephemeral live diff preview for compact/mobile surfaces.
 * Not the persisted Cleo Review model. Future review sessions should use
 * review.open* + review.getSnapshot.
 */
export const WsReviewGetDiffPreviewRpc = Rpc.make(WS_METHODS.reviewGetDiffPreview, {
  payload: ReviewDiffPreviewInput,
  success: ReviewDiffPreviewResult,
  error: Schema.Union([ReviewDiffPreviewError, EnvironmentAuthorizationError]),
});

export const WsReviewGetDiffFileContentsRpc = Rpc.make(WS_METHODS.reviewGetDiffFileContents, {
  payload: ReviewDiffFileContentsInput,
  success: ReviewDiffFileContentsResult,
  error: Schema.Union([ReviewDiffPreviewError, EnvironmentAuthorizationError]),
});

export const WsTerminalOpenRpc = Rpc.make(WS_METHODS.terminalOpen, {
  payload: TerminalOpenInput,
  success: TerminalSessionSnapshot,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalAttachRpc = Rpc.make(WS_METHODS.terminalAttach, {
  payload: TerminalAttachInput,
  success: TerminalAttachStreamEvent,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsTerminalWriteRpc = Rpc.make(WS_METHODS.terminalWrite, {
  payload: TerminalWriteInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalResizeRpc = Rpc.make(WS_METHODS.terminalResize, {
  payload: TerminalResizeInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalClearRpc = Rpc.make(WS_METHODS.terminalClear, {
  payload: TerminalClearInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalRestartRpc = Rpc.make(WS_METHODS.terminalRestart, {
  payload: TerminalRestartInput,
  success: TerminalSessionSnapshot,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsTerminalCloseRpc = Rpc.make(WS_METHODS.terminalClose, {
  payload: TerminalCloseInput,
  error: Schema.Union([TerminalError, EnvironmentAuthorizationError]),
});

export const WsPreviewOpenRpc = Rpc.make(WS_METHODS.previewOpen, {
  payload: PreviewOpenInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsCloudBrowserControlRpc = Rpc.make(WS_METHODS.cloudBrowserControl, {
  payload: CloudBrowserControlInput,
  success: CloudBrowserFrame,
  error: Schema.Union([
    CloudBrowserControlError,
    EnvironmentAuthorizationError,
    NeucleosDoorSessionError,
  ]),
});

export const WsCloudBrowserWatchRpc = Rpc.make(WS_METHODS.cloudBrowserWatch, {
  payload: Schema.Struct({ threadId: ThreadId }),
  success: CloudBrowserFrame,
  stream: true,
  error: Schema.Union([
    CloudBrowserControlError,
    EnvironmentAuthorizationError,
    NeucleosDoorSessionError,
  ]),
});

export const WsPreviewNavigateRpc = Rpc.make(WS_METHODS.previewNavigate, {
  payload: PreviewNavigateInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewResizeRpc = Rpc.make(WS_METHODS.previewResize, {
  payload: PreviewResizeInput,
  success: PreviewSessionSnapshot,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewRefreshRpc = Rpc.make(WS_METHODS.previewRefresh, {
  payload: PreviewRefreshInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewCloseRpc = Rpc.make(WS_METHODS.previewClose, {
  payload: PreviewCloseInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewListRpc = Rpc.make(WS_METHODS.previewList, {
  payload: PreviewListInput,
  success: PreviewListResult,
  error: EnvironmentAuthorizationError,
});

export const WsPreviewReportStatusRpc = Rpc.make(WS_METHODS.previewReportStatus, {
  payload: PreviewReportStatusInput,
  error: Schema.Union([PreviewError, EnvironmentAuthorizationError]),
});

export const WsPreviewAutomationConnectRpc = Rpc.make(WS_METHODS.previewAutomationConnect, {
  payload: PreviewAutomationHost,
  success: PreviewAutomationStreamEvent,
  error: Schema.Union([PreviewAutomationError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsPreviewAutomationRespondRpc = Rpc.make(WS_METHODS.previewAutomationRespond, {
  payload: PreviewAutomationResponse,
  error: Schema.Union([PreviewAutomationError, EnvironmentAuthorizationError]),
});

export const WsPreviewAutomationClaimRpc = Rpc.make(WS_METHODS.previewAutomationClaim, {
  payload: PreviewAutomationClaim,
  success: Schema.Boolean,
  error: Schema.Union([PreviewAutomationError, EnvironmentAuthorizationError]),
});

export const WsPreviewAutomationFocusHostRpc = Rpc.make(WS_METHODS.previewAutomationFocusHost, {
  payload: PreviewAutomationHostFocus,
  error: EnvironmentAuthorizationError,
});

export const WsSubscribePreviewEventsRpc = Rpc.make(WS_METHODS.subscribePreviewEvents, {
  payload: Schema.Struct({}),
  success: PreviewEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeDiscoveredLocalServersRpc = Rpc.make(
  WS_METHODS.subscribeDiscoveredLocalServers,
  {
    payload: Schema.Struct({
      configuredUrls: Schema.optional(ConfiguredLocalServerUrls),
    }),
    success: DiscoveredLocalServerList,
    error: EnvironmentAuthorizationError,
    stream: true,
  },
);

export const WsOrchestrationDispatchCommandRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.dispatchCommand,
  {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: Schema.Union([
      OrchestrationDispatchCommandError,
      NeucleosDoorSessionError,
      EnvironmentAuthorizationError,
    ]),
  },
);

export const WsOrchestrationGetWorkflowScriptRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getWorkflowScript,
  {
    payload: OrchestrationRpcSchemas.getWorkflowScript.input,
    success: OrchestrationRpcSchemas.getWorkflowScript.output,
    error: Schema.Union([OrchestrationGetWorkflowScriptError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationGetTurnDiffRpc = Rpc.make(ORCHESTRATION_WS_METHODS.getTurnDiff, {
  payload: OrchestrationGetTurnDiffInput,
  success: OrchestrationRpcSchemas.getTurnDiff.output,
  error: Schema.Union([OrchestrationGetTurnDiffError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationGetFullThreadDiffRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getFullThreadDiff,
  {
    payload: OrchestrationGetFullThreadDiffInput,
    success: OrchestrationRpcSchemas.getFullThreadDiff.output,
    error: Schema.Union([OrchestrationGetFullThreadDiffError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationSearchThreadsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.searchThreads, {
  payload: OrchestrationSearchThreadsInput,
  success: OrchestrationRpcSchemas.searchThreads.output,
  error: Schema.Union([OrchestrationSearchThreadsError, EnvironmentAuthorizationError]),
});

export const WsOrchestrationGetArchivedShellSnapshotRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot,
  {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationGetBringBackReadinessRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.getBringBackReadiness,
  {
    payload: OrchestrationRpcSchemas.getBringBackReadiness.input,
    success: OrchestrationRpcSchemas.getBringBackReadiness.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
  },
);

export const WsOrchestrationSubscribeShellRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeShell, {
  payload: OrchestrationRpcSchemas.subscribeShell.input,
  success: OrchestrationRpcSchemas.subscribeShell.output,
  error: Schema.Union([
    OrchestrationGetSnapshotError,
    NeucleosDoorSessionError,
    EnvironmentAuthorizationError,
  ]),
  stream: true,
});

export const WsOrchestrationSubscribeThreadRpc = Rpc.make(
  ORCHESTRATION_WS_METHODS.subscribeThread,
  {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: Schema.Union([
      OrchestrationGetSnapshotError,
      NeucleosDoorSessionError,
      EnvironmentAuthorizationError,
    ]),
    stream: true,
  },
);

export const WsSubscribeTerminalEventsRpc = Rpc.make(WS_METHODS.subscribeTerminalEvents, {
  payload: Schema.Struct({}),
  success: TerminalEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeTerminalMetadataRpc = Rpc.make(WS_METHODS.subscribeTerminalMetadata, {
  payload: Schema.Struct({}),
  success: TerminalMetadataStreamEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeServerConfigRpc = Rpc.make(WS_METHODS.subscribeServerConfig, {
  payload: Schema.Struct({}),
  success: ServerConfigStreamEvent,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsSubscribeServerLifecycleRpc = Rpc.make(WS_METHODS.subscribeServerLifecycle, {
  payload: Schema.Struct({}),
  success: ServerLifecycleStreamEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeAuthAccessRpc = Rpc.make(WS_METHODS.subscribeAuthAccess, {
  payload: Schema.Struct({}),
  success: AuthAccessStreamEvent,
  error: Schema.Union([AuthAccessStreamError, EnvironmentAuthorizationError]),
  stream: true,
});

export const WsSubscribeResourceTelemetryRpc = Rpc.make(WS_METHODS.subscribeResourceTelemetry, {
  payload: Schema.Struct({}),
  success: ResourceTelemetrySnapshot,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeSpotifyViewRpc = Rpc.make(WS_METHODS.subscribeSpotifyView, {
  payload: Schema.Struct({}),
  success: SpotifyEngineView,
  error: EnvironmentAuthorizationError,
  stream: true,
});

// MUS8 (register MUS-CAN-08): the audio tap — status and ~30 Hz frames from
// the native helper that hears what this Mac is playing, on one stream.
export const WsAudioTapStartRpc = Rpc.make(WS_METHODS.audioTapStart, {
  payload: AudioTapStartInput,
  success: AudioTapStatus,
  error: Schema.Union([AudioTapError, EnvironmentAuthorizationError]),
});

export const WsAudioTapStopRpc = Rpc.make(WS_METHODS.audioTapStop, {
  payload: Schema.Struct({}),
  success: AudioTapStatus,
  error: EnvironmentAuthorizationError,
});

export const WsSubscribeAudioTapRpc = Rpc.make(WS_METHODS.subscribeAudioTap, {
  payload: Schema.Struct({}),
  success: AudioTapEvent,
  error: EnvironmentAuthorizationError,
  stream: true,
});

export const WsSubscribeMusicViewRpc = Rpc.make(WS_METHODS.subscribeMusicView, {
  payload: Schema.Struct({}),
  success: MusicEngineView,
  error: Schema.Union([MusicEngineCallError, EnvironmentAuthorizationError]),
  stream: true,
});

/** Every served RPC carries the same value-safe device/World authority boundary. */
export class NeucleosDoorRpcAuthorization extends RpcMiddleware.Service<NeucleosDoorRpcAuthorization>()(
  "NeucleosDoorRpcAuthorization",
  { error: NeucleosDoorSessionError },
) {}

export const WsRpcGroup = RpcGroup.make(
  WsNeucleosDoorSessionAttachRpc,
  WsNeucleosDoorSessionRenewRpc,
  WsNeucleosDoorSessionDiscoverRpc,
  WsNeucleosDoorSessionReadContextSelectionRpc,
  WsNeucleosDoorSessionReadToolsRpc,
  WsNeucleosDoorSessionSaveToolsRpc,
  WsNeucleosDoorSessionPrepareContextSelectionRpc,
  WsNeucleosDoorLiveVoiceRpc,
  WsNeucleosDoorCanvasRpc,
  WsNeucleosDoorCanvasActionRpc,
  WsNeucleosDoorCanvasResourceRpc,
  WsNeucleosDoorCanvasDataRpc,
  WsServerProbeRpc,
  WsServerGetConfigRpc,
  WsServerRefreshProvidersRpc,
  WsServerUpdateProviderRpc,
  WsServerUpdateServerRpc,
  WsServerUpdateServerWithProgressRpc,
  WsServerUpsertKeybindingRpc,
  WsServerRemoveKeybindingRpc,
  WsServerGetSettingsRpc,
  WsServerUpdateSettingsRpc,
  WsServerGetTraceDiagnosticsRpc,
  WsServerGetProcessDiagnosticsRpc,
  WsServerGetProcessResourceHistoryRpc,
  WsServerGetResourceTelemetryHistoryRpc,
  WsServerRetryResourceTelemetryRpc,
  WsMusicSendCommandRpc,
  WsMusicSearchRpc,
  WsMusicListDevicesRpc,
  WsMusicGetLibraryRpc,
  WsMusicGetHomeRpc,
  WsMusicGetCollectionRpc,
  WsMusicCreatePlaylistRpc,
  WsServerGetUsageSummaryRpc,
  WsServerGetAccountCapacityRpc,
  WsServerSignalProcessRpc,
  WsServerReportClientActivityRpc,
  WsCloudGetRelayClientStatusRpc,
  WsCloudInstallRelayClientRpc,
  WsProjectsListEntriesRpc,
  WsProjectsReadFileRpc,
  WsProjectsSearchContentsRpc,
  WsProjectsSearchEntriesRpc,
  WsProjectsWriteFileRpc,
  WsShellOpenInEditorRpc,
  WsFilesystemBrowseRpc,
  WsAssetsCreateUrlRpc,
  WsSubscribeVcsStatusRpc,
  WsVcsPullRpc,
  WsVcsRefreshStatusRpc,
  WsGitRunStackedActionRpc,
  WsVcsListRefsRpc,
  WsVcsCreateWorktreeRpc,
  WsVcsRemoveWorktreeRpc,
  WsVcsCreateRefRpc,
  WsVcsSwitchRefRpc,
  WsVcsInitRpc,
  WsReviewGetDiffPreviewRpc,
  WsReviewGetDiffFileContentsRpc,
  WsTerminalOpenRpc,
  WsTerminalAttachRpc,
  WsTerminalWriteRpc,
  WsTerminalResizeRpc,
  WsTerminalClearRpc,
  WsTerminalRestartRpc,
  WsTerminalCloseRpc,
  WsSubscribeTerminalEventsRpc,
  WsSubscribeTerminalMetadataRpc,
  WsPreviewOpenRpc,
  WsCloudBrowserControlRpc,
  WsCloudBrowserWatchRpc,
  WsPreviewNavigateRpc,
  WsPreviewResizeRpc,
  WsPreviewRefreshRpc,
  WsPreviewCloseRpc,
  WsPreviewListRpc,
  WsPreviewReportStatusRpc,
  WsPreviewAutomationConnectRpc,
  WsPreviewAutomationClaimRpc,
  WsPreviewAutomationRespondRpc,
  WsPreviewAutomationFocusHostRpc,
  WsSubscribePreviewEventsRpc,
  WsSubscribeDiscoveredLocalServersRpc,
  WsSubscribeServerConfigRpc,
  WsSubscribeServerLifecycleRpc,
  WsSubscribeAuthAccessRpc,
  WsSubscribeResourceTelemetryRpc,
  WsSubscribeMusicViewRpc,
  WsSpotifySendCommandRpc,
  WsSpotifySearchRpc,
  WsSpotifyListDevicesRpc,
  WsSpotifyGetLibraryRpc,
  WsSpotifyGetHomeRpc,
  WsSpotifyGetCollectionRpc,
  WsSubscribeSpotifyViewRpc,
  WsAudioTapStartRpc,
  WsAudioTapStopRpc,
  WsSubscribeAudioTapRpc,
  WsOrchestrationDispatchCommandRpc,
  WsOrchestrationGetWorkflowScriptRpc,
  WsOrchestrationGetTurnDiffRpc,
  WsOrchestrationGetFullThreadDiffRpc,
  WsOrchestrationSearchThreadsRpc,
  WsOrchestrationGetArchivedShellSnapshotRpc,
  WsOrchestrationGetBringBackReadinessRpc,
  WsOrchestrationSubscribeShellRpc,
  WsOrchestrationSubscribeThreadRpc,
  // The dictation module keeps its own group; merging it here is what puts it
  // on the served `/ws` surface (apps/server/src/speech serves the handlers).
)
  .merge(
    DictationRpcGroup.omit(WsDictationStartRpc._tag).add(
      WsDictationStartRpc.setError(
        Schema.Union([WsDictationStartRpc.errorSchema, NeucleosDoorSessionError]),
      ),
    ),
  )
  // Readback (Cleo speaks) rides the same seam: its group lives in
  // readback.ts, its handlers in apps/server/src/speech.
  .merge(
    ReadbackRpcGroup.omit(WsReadbackSpeakRpc._tag).add(
      WsReadbackSpeakRpc.setError(
        Schema.Union([WsReadbackSpeakRpc.errorSchema, NeucleosDoorSessionError]),
      ),
    ),
  )
  .merge(LocalModelsRpcGroup)
  .merge(WorldWatchRpcGroup)
  .merge(CodexAccountRpcGroup)
  .merge(ClaudeAccountSignInRpcGroup)
  .middleware(NeucleosDoorRpcAuthorization);
